import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { Save, ArrowBack, Schedule } from '@mui/icons-material';
import axios from 'axios';

const ScheduleInterview = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [application, setApplication] = useState(null);
  const [existingInterviews, setExistingInterviews] = useState([]);
  const [interviewData, setInterviewData] = useState({
    meetingLink: '',
    scheduledDate: '',
    duration: 60,
    interviewers: '',
    interviewType: 'technical',
    notes: '',
    instructions: ''
  });

  const interviewTypes = [
    { value: 'technical', label: 'Technical Interview' },
    { value: 'hr', label: 'HR Interview' },
    { value: 'behavioral', label: 'Behavioral Interview' },
    { value: 'cultural', label: 'Cultural Fit Interview' },
    { value: 'final', label: 'Final Interview' },
    { value: 'screening', label: 'Screening Interview' }
  ];

  const durationOptions = [30, 45, 60, 90, 120];

  useEffect(() => {
    fetchApplication();
  }, [applicationId]);

  useEffect(() => {
    if (application?.candidate?._id) {
      fetchExistingInterviews();
    }
  }, [application]);

  const fetchApplication = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `http://localhost:5000/api/applications/${applicationId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setApplication(response.data.data);
      
      // Set default date to 2 days from now
      const now = new Date();
      now.setDate(now.getDate() + 2);
      setInterviewData(prev => ({
        ...prev,
        scheduledDate: now.toISOString().slice(0, 16)
      }));
    } catch (err) {
      console.error('Error fetching application:', err);
      setError('Failed to load application details');
    } finally {
      setLoading(false);
    }
  };

  const fetchExistingInterviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `http://localhost:5000/api/interviews?candidateId=${application.candidate._id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setExistingInterviews(response.data.data || []);
    } catch (err) {
      console.error('Error fetching existing interviews:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    // Validate required fields
    if (!interviewData.meetingLink.trim()) {
      setError('Meeting link is required');
      setSaving(false);
      return;
    }

    if (!interviewData.scheduledDate) {
      setError('Scheduled date is required');
      setSaving(false);
      return;
    }

    if (!interviewData.interviewers.trim()) {
      setError('At least one interviewer is required');
      setSaving(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const interviewPayload = {
        candidate: application.candidate._id,
        application: application._id,
        job: application.job._id,
        meetingLink: interviewData.meetingLink,
        scheduledDate: new Date(interviewData.scheduledDate),
        duration: interviewData.duration,
        interviewers: interviewData.interviewers.split(',').map(i => i.trim()).filter(i => i.length > 0),
        interviewType: interviewData.interviewType,
        notes: interviewData.notes,
        instructions: interviewData.instructions
      };

      console.log('Application data:', application);
      console.log('Sending interview payload:', interviewPayload);
      
      const response = await axios.post(
        'http://localhost:5000/api/interviews',
        interviewPayload,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      console.log('Interview creation response:', response.data);

      setSuccess('Interview scheduled successfully!');
      
      // Update application status to interview_scheduled
      await axios.put(
        `http://localhost:5000/api/applications/admin/${applicationId}/status`,
        { 
          status: 'interview_scheduled',
          notes: 'Interview scheduled via interview scheduling page'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Refresh existing interviews list
      await fetchExistingInterviews();
      
      // Reset form
      setInterviewData({
        meetingLink: '',
        scheduledDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16),
        duration: 60,
        interviewers: '',
        interviewType: 'technical',
        notes: '',
        instructions: ''
      });
    } catch (error) {
      console.error('Error scheduling interview:', error);
      if (error.response?.data?.error) {
        setError(`Failed to schedule interview: ${error.response.data.error}`);
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to schedule interview. Please check all required fields.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setInterviewData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRescheduleInterview = (interview) => {
    // Pre-fill the form with the interview data for rescheduling
    setInterviewData({
      meetingLink: interview.meetingLink,
      scheduledDate: new Date(interview.scheduledDate).toISOString().slice(0, 16),
      duration: interview.duration,
      interviewers: interview.interviewers.join(', '),
      interviewType: interview.interviewType,
      notes: interview.notes || '',
      instructions: interview.instructions || ''
    });
    
    // Scroll to the form
    document.getElementById('interview-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!application) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Application not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/applications')}
          sx={{ mr: 2 }}
        >
          Back to Applications
        </Button>
        <Typography variant="h4" component="h1">
          Schedule Interview
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Application Details */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Application Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Candidate:</strong>
              </Typography>
              <Typography variant="body1" gutterBottom>
                {application.candidate.profile?.firstName} {application.candidate.profile?.lastName}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Email:</strong>
              </Typography>
              <Typography variant="body1" gutterBottom>
                {application.candidate.email}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Job Position:</strong>
              </Typography>
              <Typography variant="body1" gutterBottom>
                {application.job.title}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Department:</strong>
              </Typography>
              <Typography variant="body1" gutterBottom>
                {application.job.department}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Current Status:</strong>
              </Typography>
              <Typography variant="body1" gutterBottom>
                {application.status.replace('_', ' ').toUpperCase()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Existing Interviews */}
        {existingInterviews.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Existing Interviews
              </Typography>
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                {existingInterviews.map((interview) => (
                  <Box key={interview._id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {interview.interviewType.charAt(0).toUpperCase() + interview.interviewType.slice(1)} Interview
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(interview.scheduledDate).toLocaleDateString()} at {new Date(interview.scheduledDate).toLocaleTimeString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Duration: {interview.duration} minutes
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Interviewers: {interview.interviewers.join(', ')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Status: {interview.status.charAt(0).toUpperCase() + interview.status.slice(1)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => window.open(interview.meetingLink, '_blank')}
                        >
                          Join Meeting
                        </Button>
                        {interview.status === 'scheduled' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleRescheduleInterview(interview)}
                          >
                            Reschedule
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Interview Form */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Schedule New Interview
            </Typography>
            
            <Box component="form" onSubmit={handleSubmit} id="interview-form">
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Interview Type</InputLabel>
                    <Select
                      value={interviewData.interviewType}
                      onChange={(e) => handleInputChange('interviewType', e.target.value)}
                      label="Interview Type"
                    >
                      {interviewTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Duration (minutes)</InputLabel>
                    <Select
                      value={interviewData.duration}
                      onChange={(e) => handleInputChange('duration', e.target.value)}
                      label="Duration (minutes)"
                    >
                      {durationOptions.map((duration) => (
                        <MenuItem key={duration} value={duration}>
                          {duration} minutes
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Scheduled Date & Time"
                    type="datetime-local"
                    value={interviewData.scheduledDate}
                    onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                    required
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Meeting Link"
                    value={interviewData.meetingLink}
                    onChange={(e) => handleInputChange('meetingLink', e.target.value)}
                    required
                    placeholder="https://meet.google.com/..."
                    helperText="Google Meet, Zoom, or other video conferencing link"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Interviewers"
                    value={interviewData.interviewers}
                    onChange={(e) => handleInputChange('interviewers', e.target.value)}
                    required
                    placeholder="John Doe, Jane Smith"
                    helperText="Enter interviewer names separated by commas"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Interview Notes"
                    value={interviewData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    multiline
                    rows={3}
                    placeholder="Any specific notes or requirements for this interview..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Instructions for Candidate"
                    value={interviewData.instructions}
                    onChange={(e) => handleInputChange('instructions', e.target.value)}
                    multiline
                    rows={3}
                    placeholder="Instructions to be sent to the candidate..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/admin/applications')}
                      disabled={saving}
                    >
                      Back to Applications
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={20} /> : <Schedule />}
                      disabled={saving}
                    >
                      {saving ? 'Scheduling...' : 'Schedule Interview'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default ScheduleInterview;
